import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Select, MenuItem, InputLabel, FormControl, Chip, Typography,
  Box, CircularProgress, Switch, FormControlLabel, Stack, Alert, LinearProgress,
  Card, CardContent, Divider,
} from '@mui/material';

const API = 'https://portal.peaksoftcon.com:8081/api/bot';

// Bot backend'in registry'si otoritedir (GET /api/bot/scrapers). Bu liste
// yalnizca ilk render ve bot'a ulasilamadigi durum icin yedektir — yeni bir
// teknokent eklerken burayi da guncelle, ama tek zorunlu yer registry.js.
const FALLBACK_SCRAPER_OPTIONS = [
  { key: 'hacettepe', label: 'Hacettepe Teknokent' },
  { key: 'teknoparkankara', label: 'Teknopark Ankara' },
  { key: 'harran', label: 'Harran Teknopark' },
];

const DEFAULT_SCRAPER_KEY = 'hacettepe';

const STATUS_COLORS = {
  queued: 'default',
  running: 'info',
  success: 'success',
  failed: 'error',
};

const blankForm = {
  id: null, name: '', scraperKey: DEFAULT_SCRAPER_KEY,
  username: '', password: '', notes: '', active: true,
  defaultStartTime: '09:00', defaultEndTime: '18:00',
  postProcessEnabled: true,
};

function StatusChip({ status }) {
  if (!status) return <Chip size="small" label="—" />;
  return <Chip size="small" color={STATUS_COLORS[status] || 'default'} label={status} />;
}

function isProcessedFile(name) {
  return /-tamamlanmis\.xlsx$/i.test(name || '');
}

export default function AdminBotPanel() {
  const [companies, setCompanies] = useState([]);
  const [scraperOptions, setScraperOptions] = useState(FALLBACK_SCRAPER_OPTIONS);
  // Map<companyId, { latest: run, files: [] }>
  const [byCompany, setByCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [isEditing, setIsEditing] = useState(false);
  const [savingForm, setSavingForm] = useState(false);
  const [runningIds, setRunningIds] = useState(new Set());
  const [historyCompany, setHistoryCompany] = useState(null);
  const [historyRuns, setHistoryRuns] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const pollTimer = useRef(null);

  // Tabloda ham anahtar ('teknoparkankara') yerine okunur ad gosterilsin.
  const scraperLabel = useCallback(
    (key) => (scraperOptions.find((o) => o.key === key) || {}).label || key || '—',
    [scraperOptions],
  );

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/companies`);
      setCompanies(data || []);
      const entries = await Promise.all(
        (data || []).map(async (c) => {
          try {
            const { data: runs } = await axios.get(`${API}/companies/${c.id}/runs`);
            const latest = (runs && runs[0]) || null;
            let files = [];
            if (latest && latest.status === 'success') {
              try {
                const { data: detail } = await axios.get(`${API}/runs/${encodeURIComponent(latest.runKey)}`);
                files = detail.files || [];
              } catch { /* */ }
            }
            return [c.id, { latest, files }];
          } catch {
            return [c.id, { latest: null, files: [] }];
          }
        }),
      );
      setByCompany(Object.fromEntries(entries));
    } catch (err) {
      console.error('failed to load companies', err);
      alert('Firmalar yüklenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll batch status while a batch is running.
  const pollBatchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/run-all/status`);
      setBatchStatus(data);
      // Refresh companies/files in tandem.
      loadCompanies();
      if (!data.isProcessing) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    } catch (err) {
      console.error('batch status poll failed', err);
    }
  }, [loadCompanies]);

  // Teknokent listesi bot registry'sinden gelir; ulasilamazsa yedek liste
  // ekranda kalir, boylece panel bot kapaliyken de acilabiliyor.
  useEffect(() => {
    axios.get(`${API}/scrapers`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setScraperOptions(data);
      })
      .catch(() => { /* yedek liste yeterli */ });
  }, []);

  useEffect(() => {
    loadCompanies();
    // Pick up an in-progress batch (e.g. user reloads mid-run)
    axios.get(`${API}/run-all/status`).then(({ data }) => {
      setBatchStatus(data);
      if (data.isProcessing && !pollTimer.current) {
        pollTimer.current = setInterval(pollBatchStatus, 5000);
      }
    }).catch(() => {});
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [loadCompanies, pollBatchStatus]);

  const openCreate = () => {
    setIsEditing(false);
    setForm(blankForm);
    setFormOpen(true);
  };

  const openEdit = (company) => {
    setIsEditing(true);
    setForm({
      id: company.id,
      name: company.name || '',
      scraperKey: company.scraperKey || DEFAULT_SCRAPER_KEY,
      username: '',
      password: '',
      notes: company.notes || '',
      active: company.active !== false,
      defaultStartTime: company.defaultStartTime || '09:00',
      defaultEndTime: company.defaultEndTime || '18:00',
      postProcessEnabled: company.postProcessEnabled !== false,
    });
    setFormOpen(true);
  };

  const closeForm = () => { if (!savingForm) setFormOpen(false); };

  const onFormChange = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const submitForm = async () => {
    if (!form.name.trim() || !form.scraperKey) {
      alert('Firma adı ve teknokent seçimi zorunlu.');
      return;
    }
    if (!isEditing && (!form.username || !form.password)) {
      alert('Yeni firma için kullanıcı adı ve şifre zorunlu.');
      return;
    }
    setSavingForm(true);
    try {
      const base = {
        name: form.name, scraperKey: form.scraperKey, notes: form.notes,
        active: form.active, defaultStartTime: form.defaultStartTime,
        defaultEndTime: form.defaultEndTime, postProcessEnabled: form.postProcessEnabled,
      };
      if (isEditing) {
        if (form.username) base.username = form.username;
        if (form.password) base.password = form.password;
        await axios.put(`${API}/companies/${form.id}`, base);
      } else {
        await axios.post(`${API}/companies`, {
          ...base, username: form.username, password: form.password,
        });
      }
      setFormOpen(false);
      await loadCompanies();
    } catch (err) {
      alert('Kaydetme hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingForm(false);
    }
  };

  const removeCompany = async (company) => {
    if (!window.confirm(`"${company.name}" firmasını silmek istediğine emin misin?`)) return;
    try {
      await axios.delete(`${API}/companies/${company.id}`);
      await loadCompanies();
    } catch (err) {
      alert('Silme hatası: ' + (err.response?.data?.error || err.message));
    }
  };

  const triggerRun = async (company) => {
    setRunningIds((s) => new Set(s).add(company.id));
    try {
      await axios.post(`${API}/companies/${company.id}/run`, {});
      setTimeout(() => loadCompanies(), 1500);
      setTimeout(() => loadCompanies(), 6000);
      setTimeout(() => loadCompanies(), 30000);
    } catch (err) {
      alert('Çalıştırma hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setTimeout(() => {
        setRunningIds((s) => { const n = new Set(s); n.delete(company.id); return n; });
      }, 1500);
    }
  };

  const triggerRunAll = async () => {
    const activeCount = companies.filter((c) => c.active).length;
    if (activeCount === 0) {
      alert('Aktif firma yok.');
      return;
    }
    const minutes = Math.round(activeCount * 5);
    if (!window.confirm(
      `${activeCount} firma sırayla çalıştırılacak (firmalar arası 10 saniye bekleme).\n` +
      `Tahmini toplam süre: ~${minutes} dakika.\n` +
      `Hata alan firmalar sonda bir kez daha denenecek.\n\nDevam edilsin mi?`
    )) return;
    try {
      const { data } = await axios.post(`${API}/run-all`, { delayBetweenMs: 10000 });
      setBatchStatus({ isProcessing: true, batch: { startedAt: new Date(), total: data.total, completed: 0, failedFirstPass: [], failedFinal: [] } });
      if (!pollTimer.current) {
        pollTimer.current = setInterval(pollBatchStatus, 5000);
      }
    } catch (err) {
      alert('Toplu çalıştırma başlatılamadı: ' + (err.response?.data?.error || err.message));
    }
  };

  const openHistory = async (company) => {
    setHistoryCompany(company);
    setHistoryRuns([]);
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/companies/${company.id}/runs`);
      setHistoryRuns(data || []);
    } catch (err) {
      alert('Geçmiş yüklenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setHistoryLoading(false);
    }
  };

  const downloadFile = async (run, file) => {
    try {
      const url = `${API}/runs/${encodeURIComponent(run.runKey)}/files/${encodeURIComponent(file.fileName)}`;
      const { data } = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      alert('İndirme hatası: ' + (err.response?.data?.error || err.message));
    }
  };

  const batchActive = batchStatus?.isProcessing;
  const b = batchStatus?.batch;

  // Display-only "Sıra No": companies shown alphabetically and numbered 1..N.
  // The bot's real id (c.id) is untouched — every run/file/history call still
  // uses c.id, so nothing breaks; only the column users read changes.
  const sortedCompanies = useMemo(
    () => [...companies].sort(
      (a, b2) => (a.name || '').localeCompare(b2.name || '', 'tr'),
    ),
    [companies],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={2}
        flexWrap="wrap"
      >
        <Typography variant="h4" sx={{ flex: { sm: 1 } }}>Bot Yönetimi</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button onClick={loadCompanies} disabled={loading} size="small" variant="outlined">
            {loading ? <CircularProgress size={16} /> : 'Yenile'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={triggerRunAll}
            disabled={batchActive}
          >
            {batchActive ? 'Tümü çalışıyor...' : '▶ Tümünü Çalıştır'}
          </Button>
          <Button variant="contained" onClick={openCreate}>+ Yeni</Button>
        </Stack>
      </Stack>

      {batchActive && b && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Toplu çalıştırma: <strong>{b.completed}/{b.total}</strong> tamamlandı
            {b.inProgressId ? ` — şu an firma #${b.inProgressId}` : ''}
            {b.failedFirstPass?.length > 0 ? ` — ${b.failedFirstPass.length} fail (sonda retry)` : ''}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (b.completed / Math.max(1, b.total)) * 100)}
            sx={{ mt: 1 }}
          />
        </Alert>
      )}
      {!batchActive && b?.finishedAt && b.failedFinal?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Son toplu çalıştırmada {b.failedFinal.length} firma retry'da da hata aldı: {b.failedFinal.join(', ')}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Şifreler AES-256-GCM ile master key altında tutulur (sunucuda ayrı dosya, botuser, 0400).
        Düzenlerken kullanıcı adı/şifre alanlarını boş bırakırsan değişmez.
      </Typography>

      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Sıra No</TableCell>
              <TableCell>Firma</TableCell>
              <TableCell>Teknokent</TableCell>
              <TableCell>Aktif</TableCell>
              <TableCell>Son Run</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Son Dosyalar</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">Henüz firma eklenmemiş.</TableCell>
              </TableRow>
            )}
            {sortedCompanies.map((c, index) => {
              const detail = byCompany[c.id] || { latest: null, files: [] };
              const last = detail.latest;
              const isRunning = runningIds.has(c.id) || last?.status === 'running' || (batchActive && b?.inProgressId === c.id);
              const original = (detail.files || []).find((f) => !isProcessedFile(f.fileName));
              const processed = (detail.files || []).find((f) => isProcessedFile(f.fileName));
              return (
                <TableRow key={c.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <strong>{c.name}</strong>
                    {c.notes && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {c.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{scraperLabel(c.scraperKey)}</TableCell>
                  <TableCell>{c.active ? 'Evet' : 'Hayır'}</TableCell>
                  <TableCell>
                    {last ? new Date(last.startedAt || last.createdAt).toLocaleString('tr-TR') : '—'}
                  </TableCell>
                  <TableCell><StatusChip status={last?.status} /></TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      {original && (
                        <Button size="small" variant="text" onClick={() => downloadFile(last, original)}>
                          📄 Orijinal
                        </Button>
                      )}
                      {processed && (
                        <Button size="small" variant="text" color="warning" onClick={() => downloadFile(last, processed)}>
                          ✨ Tamamlanmış
                        </Button>
                      )}
                      {!original && !processed && <Typography variant="caption" color="text.secondary">—</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small" variant="contained" color="primary"
                        onClick={() => triggerRun(c)}
                        disabled={isRunning || !c.active || batchActive}
                      >
                        {isRunning ? <CircularProgress size={14} color="inherit" /> : 'Çalıştır'}
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => openHistory(c)}>Geçmiş</Button>
                      <Button size="small" variant="outlined" onClick={() => openEdit(c)} disabled={batchActive}>Düzenle</Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => removeCompany(c)} disabled={batchActive}>Sil</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile card grid */}
      <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {companies.length === 0 && !loading && (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            Henüz firma eklenmemiş.
          </Typography>
        )}
        {sortedCompanies.map((c, index) => {
          const detail = byCompany[c.id] || { latest: null, files: [] };
          const last = detail.latest;
          const isRunning = runningIds.has(c.id) || last?.status === 'running' || (batchActive && b?.inProgressId === c.id);
          const original = (detail.files || []).find((f) => !isProcessedFile(f.fileName));
          const processed = (detail.files || []).find((f) => isProcessedFile(f.fileName));
          return (
            <Card key={c.id}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6">{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{index + 1} • {scraperLabel(c.scraperKey)} • {c.active ? 'Aktif' : 'Pasif'}
                    </Typography>
                    {c.notes && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {c.notes}
                      </Typography>
                    )}
                  </Box>
                  <StatusChip status={last?.status} />
                </Stack>

                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Son run: {last ? new Date(last.startedAt || last.createdAt).toLocaleString('tr-TR') : '—'}
                </Typography>

                {(original || processed) && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                    {original && (
                      <Button size="small" variant="outlined" onClick={() => downloadFile(last, original)}>
                        📄 Orijinal
                      </Button>
                    )}
                    {processed && (
                      <Button size="small" variant="outlined" color="warning" onClick={() => downloadFile(last, processed)}>
                        ✨ Tamamlanmış
                      </Button>
                    )}
                  </Stack>
                )}

                <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    size="small" variant="contained" color="primary"
                    onClick={() => triggerRun(c)}
                    disabled={isRunning || !c.active || batchActive}
                    fullWidth
                  >
                    {isRunning ? <CircularProgress size={14} color="inherit" /> : 'Çalıştır'}
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => openHistory(c)} fullWidth>Geçmiş</Button>
                  <Button size="small" variant="outlined" onClick={() => openEdit(c)} disabled={batchActive} fullWidth>Düzenle</Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => removeCompany(c)} disabled={batchActive} fullWidth>Sil</Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Firmayı Düzenle' : 'Yeni Firma Ekle'}</DialogTitle>
        <DialogContent>
          <TextField label="Firma Adı" value={form.name} onChange={onFormChange('name')}
            fullWidth margin="dense" required />
          <FormControl fullWidth margin="dense" required>
            <InputLabel id="bot-scraper-label">Teknokent</InputLabel>
            <Select labelId="bot-scraper-label" label="Teknokent"
              value={form.scraperKey} onChange={onFormChange('scraperKey')}>
              {scraperOptions.map((o) => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label={isEditing ? 'Kullanıcı Adı (boş bırak = değiştirme)' : 'Kullanıcı Adı'}
            value={form.username} onChange={onFormChange('username')}
            fullWidth margin="dense" required={!isEditing} autoComplete="off"
          />
          <TextField
            label={isEditing ? 'Şifre (boş bırak = değiştirme)' : 'Şifre'}
            type="password" value={form.password} onChange={onFormChange('password')}
            fullWidth margin="dense" required={!isEditing} autoComplete="new-password"
          />
          <Stack direction="row" spacing={2} mt={1}>
            <TextField label="Default Giriş" type="time"
              value={form.defaultStartTime} onChange={onFormChange('defaultStartTime')}
              fullWidth margin="dense" InputLabelProps={{ shrink: true }} />
            <TextField label="Default Çıkış" type="time"
              value={form.defaultEndTime} onChange={onFormChange('defaultEndTime')}
              fullWidth margin="dense" InputLabelProps={{ shrink: true }} />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">
            Eksik haftaiçi günler bu saatlerle doldurulur (post-process açıkken).
          </Typography>
          <TextField label="Notlar (opsiyonel)" value={form.notes} onChange={onFormChange('notes')}
            fullWidth margin="dense" multiline minRows={2} />
          <FormControlLabel
            control={<Switch checked={form.active} onChange={onFormChange('active')} />}
            label="Aktif (Tümünü Çalıştır'a dahil)" />
          <FormControlLabel
            control={<Switch checked={form.postProcessEnabled} onChange={onFormChange('postProcessEnabled')} />}
            label="Excel post-process (eksik günleri doldur)" />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={savingForm}>İptal</Button>
          <Button onClick={submitForm} variant="contained" disabled={savingForm}>
            {savingForm ? <CircularProgress size={18} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run history dialog */}
      <Dialog open={!!historyCompany} onClose={() => setHistoryCompany(null)} maxWidth="md" fullWidth>
        <DialogTitle>Run Geçmişi — {historyCompany?.name}</DialogTitle>
        <DialogContent>
          {historyLoading && <CircularProgress />}
          {!historyLoading && historyRuns.length === 0 && (
            <Typography color="text.secondary">Bu firma için henüz run yok.</Typography>
          )}
          {!historyLoading && historyRuns.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Başlangıç</TableCell>
                  <TableCell>Bitiş</TableCell>
                  <TableCell>Tetikleyici</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Hata / Detay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRuns.map((r) => (
                  <TableRow key={r.runKey}>
                    <TableCell>{r.startedAt ? new Date(r.startedAt).toLocaleString('tr-TR') : '—'}</TableCell>
                    <TableCell>{r.finishedAt ? new Date(r.finishedAt).toLocaleString('tr-TR') : '—'}</TableCell>
                    <TableCell>{r.triggerType}</TableCell>
                    <TableCell><StatusChip status={r.status} /></TableCell>
                    <TableCell style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.errorMessage || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryCompany(null)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
